import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useComponentAccess } from '../../hooks/useComponentAccess';

interface ComponentProtectedRouteProps {
  children: React.ReactNode;
  // Pass a single name OR an array (OR-logic: access granted if user can access ANY).
  componentName: string | string[];
  requiredAccess?: 'view' | 'edit' | 'manage' | 'restricted';
  // When true, CRM 'restricted' (salesperson) users are also allowed in even
  // without the listed component access — the page itself scopes their data to
  // their assigned clients (e.g. Order Requests).
  allowCrmRestricted?: boolean;
}

const ComponentProtectedRoute: React.FC<ComponentProtectedRouteProps> = ({
  children,
  componentName,
  requiredAccess = 'view',
  allowCrmRestricted = false
}) => {
  const { user, loading } = useAuth();
  const { hasAccess, isLoading, isCrmRestricted } = useComponentAccess();
  const location = useLocation();

  if (loading || isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // OR-logic: the user is allowed in if they have access to ANY of the listed components.
  const names = Array.isArray(componentName) ? componentName : [componentName];
  const allowed =
    names.some(name => hasAccess(name, requiredAccess)) ||
    (allowCrmRestricted && isCrmRestricted);

  if (!allowed) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Access Denied</AlertTitle>
          You do not have permission to access this page. ({requiredAccess} access required)
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ComponentProtectedRoute;
